// src/components/Sidebar.tsx
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback, useRef } from "react";
import { getButtonStyle } from "@/lib/globalStore";
import { useQueryClient } from '@tanstack/react-query';
import { useEffect as useEffect2, useState as useState2 } from 'react';
import { clearAuth, getAccessToken, refreshAccessToken, setAccessToken, logout as apiLogout } from "@/lib/auth";
import Button from "@/components/Buttons";
import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";
import { setLanguage } from "@/lib/i18n";
import { SUPPORTED_LOCALES, detectLocale, getLocaleOption } from "@/lib/locales";
import ConfirmDialog from "@/components/ConfirmDialog";
import VersionDisplay from "@/components/VersionDisplay";
import { scootersManagementTableKey } from "@/lib/scooterQueryHelpers";

const API_URL = import.meta.env.VITE_API_BASE;

const items = [
  { to: "/scooters", key: "scooters", icon: "🛵" },
  { to: "/clients",  key: "clients",  icon: "👤" },
  { to: "/contracts", key: "contracts", icon: "📋" },
  { to: "/finance", key: "finance", icon: "💹" },
  { to: "/how-it-works", key: "howItWorks", icon: "📘" },
] as const;

export default function Sidebar() {
  const { t, i18n } = useTranslation(['sidebar', 'common', 'theme']);
  const { pathname } = useLocation();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [user, setUser] = useState<any | null>(null);
  const [orgs, setOrgs] = useState<any[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string | undefined>(localStorage.getItem('orgId') ?? undefined);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const toggleCollapse = () => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
    localStorage.setItem('sidebar-collapsed', newCollapsed.toString());
  };

  const prefetch = useCallback((to: string) => {
    // Prefetch list queries when user hovers/focuses links to avoid eager fetching
    if (to === '/scooters' && selectedOrg) {
      qc.prefetchQuery({
        queryKey: scootersManagementTableKey(selectedOrg),
        queryFn: () =>
          import('@/api/scooters').then((m) =>
            m.listAllScooters({ orgId: selectedOrg }).then((items) => ({ items, nextCursor: null as string | null }))
          ),
        staleTime: 1000 * 60 * 5,
      });
    }
    if (to === '/clients') qc.prefetchQuery({ queryKey: ['clients', { orgId: selectedOrg }], queryFn: () => import('@/api/clients').then(m => m.listClients({ orgId: selectedOrg })), staleTime: 1000 * 60 * 5 });
    if (to === '/contracts') qc.prefetchQuery({ queryKey: ['contracts', { orgId: selectedOrg }], queryFn: () => import('@/api/contracts').then(m => m.listContracts({ orgId: selectedOrg as any })), staleTime: 1000 * 60 * 5 });
    // Do not prefetch management pages to avoid triggering org settings loads
  }, [qc, selectedOrg]);

  useEffect(() => {
    async function fetchMe() {
      let token = getAccessToken();
      if (!token) {
        token = await refreshAccessToken() || null;
        if (token) setAccessToken(token);
      }
      if (!token) return setUser(null);
      try {
        const res = await fetch(`${API_URL}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return setUser(null);
        const body = await res.json();
        setUser(body);
        // if superadmin, use orgs from response
        if (body.orgs) {
          setOrgs(body.orgs);
          if (body.orgs.length > 0) {
            const defaultOrgId = body.orgs[0].id;
            setSelectedOrg(defaultOrgId);
            localStorage.setItem('orgId', defaultOrgId); // Always set orgId if available
            try {
              const defaultOrgName = body.orgs[0].name;
              if (defaultOrgName) localStorage.setItem('orgName', defaultOrgName);
            } catch {}
          }
        } else if (body.orgId) {
          // For non-superadmins, ensure their orgId is set
          setSelectedOrg(body.orgId);
          localStorage.setItem('orgId', body.orgId);
          // Also store the org name for Home badge consistency
          try {
            const n = body.org?.name;
            if (n) localStorage.setItem('orgName', n);
          } catch {}
        }
      } catch (e) { setUser(null); }
    }
    fetchMe();
  }, []);

  // Theme toggle moved into sidebar — implement local ThemeToggle here to avoid importing App's removed component
  const [theme, setTheme] = useState2(() => (localStorage.getItem("theme") as "dark" | "light" | "blue" | "wood") || "dark");
  useEffect2(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  function ThemeToggle() {
    const { t: tTheme } = useTranslation(['theme', 'common']);
    const modes: Array<'dark' | 'light' | 'blue' | 'wood'> = ['dark', 'light', 'blue', 'wood'];
    // Get the current button style from global settings, default to 'original'
    const [currentButtonStyle, setCurrentButtonStyle] = useState(getButtonStyle() || 'original');
    const [fontSize, setFontSize] = useState('text-xs');
    const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
    
    // Listen for button style changes to update the theme buttons
    useEffect(() => {
      const handleButtonStyleChange = () => {
        setCurrentButtonStyle(getButtonStyle() || 'original');
      };
      
      window.addEventListener('buttonStyleChanged', handleButtonStyleChange);
      return () => window.removeEventListener('buttonStyleChanged', handleButtonStyleChange);
    }, []);
    
    // Dynamically adjust font size based on content width
    useEffect(() => {
      const checkFit = () => {
        if (buttonRefs.current.length === 0) return;
        
        // Get the container width (sidebar width minus padding)
        const sidebarWidth = isCollapsed ? 64 : 224; // w-16 = 64px, w-56 = 224px
        const containerPadding = 16; // px-2 on each side = 16px total
        const availableContainerWidth = sidebarWidth - containerPadding;
        
        // Calculate total width needed for all buttons
        let totalTextWidth = 0;
        const buttonPadding = 16; // px-3 on each side = ~16px per button
        const buttonGaps = 0; // buttons are connected, no gaps
        
        buttonRefs.current.forEach(btn => {
          if (btn) {
            const text = btn.textContent || '';
            // Measure text width with text-xs (12px) font
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            if (context) {
              context.font = '600 12px system-ui'; // text-xs equivalent
              const width = context.measureText(text).width;
              totalTextWidth += width + buttonPadding;
            }
          }
        });
        
        // Calculate required font size to fit
        const totalAvailableWidth = availableContainerWidth - buttonGaps;
        const scaleFactor = totalAvailableWidth / totalTextWidth;
        
        if (scaleFactor < 0.75) {
          // Need to reduce significantly
          setFontSize('text-[9px]');
        } else if (scaleFactor < 0.85) {
          setFontSize('text-[10px]');
        } else if (scaleFactor < 0.95) {
          setFontSize('text-[11px]');
        } else {
          setFontSize('text-xs');
        }
      };
      
      checkFit();
      // Re-check after a short delay to ensure DOM is ready
      const timer = setTimeout(checkFit, 150);
      return () => clearTimeout(timer);
    }, [theme, isCollapsed, i18n.language]);
    
    return (
      <div className="flex items-center gap-3" data-testid="sidebar-theme-toggle">
        <div className="inline-flex items-center rounded-lg bg-bg-e2 border border-border-subtle shadow-sm">
          {modes.map((m, i) => (
            <Button
              key={m}
              ref={(el) => { buttonRefs.current[i] = el; }}
              variant="toggle"
              active={theme === m}
              size="xs"
              buttonStyle={currentButtonStyle}
              data-testid={`sidebar-theme-${m}`}
              className={`${i === 0 ? 'rounded-l-lg' : i === modes.length - 1 ? 'rounded-r-lg' : ''} rounded-none border-0 font-semibold tracking-tight whitespace-nowrap ${fontSize}`}
              onClick={() => setTheme(m)}
              title={`${tTheme('common:theme')}: ${tTheme(`theme:${m}`)}`}
            >
              {tTheme(`theme:${m}`)}
            </Button>
          ))}
        </div>
      </div>
    );
  }

  function LanguageSwitcher() {
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const currentLang = (i18n.resolvedLanguage || i18n.language || 'en');
    const current = detectLocale(currentLang);
    
    // Close dropdown when clicking outside
    useEffect(() => {
      if (!open) return;
      
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setOpen(false);
        }
      };
      
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);
    
    return (
      <div className="relative" ref={dropdownRef} data-testid="sidebar-language-switcher">
        <Button
          variant="ghost"
          size="sm"
          className="h-9 px-2"
          onClick={() => setOpen((v) => !v)}
          title={t('common:language')}
          data-testid="sidebar-language-switcher-button"
        >
          <Globe className="size-4 mr-2" />
          <span className="text-sm">
            {getLocaleOption(current).name}
          </span>
        </Button>
        {open && (
          <div data-testid="sidebar-language-dropdown" className="absolute left-0 bottom-10 mb-1 w-36 rounded-md border border-border-subtle bg-bg-elevated shadow-lg z-50">
            {SUPPORTED_LOCALES.map((locale) => (
              <button
                key={locale.code}
                data-testid={`sidebar-language-option-${locale.code}`}
                className="w-full text-left px-3 py-2 text-sm hover:bg-accent-cyan/10"
                onClick={() => { setLanguage(locale.code); setOpen(false); }}
              >
                {locale.flag} {locale.name}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  useEffect(() => {
    async function loadOrgs() {
      let token = getAccessToken();
      if (!token) {
        token = await refreshAccessToken() || null;
        if (token) setAccessToken(token);
      }
      if (!token) return;
      try {
        const res = await fetch(`${API_URL}/orgs`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const body = await res.json();
        const list = body.items || [];
        setOrgs(list);
        // Ensure orgName reflects the currently selected org for superadmins
        try {
          const currentId = localStorage.getItem('orgId') ?? undefined;
          if (currentId) {
            const match = list.find((o: any) => o.id === currentId);
            if (match?.name) localStorage.setItem('orgName', match.name);
          }
        } catch {}
      } catch (e) { /* ignore */ }
    }
    if (user?.role === 'superadmin') loadOrgs();
  }, [user]);

  async function onLogout() {
    setLogoutConfirmOpen(true);
  }

  async function handleConfirmLogout() {
    await apiLogout();
    clearAuth();
    setUser(null); 
    qc.clear();
    nav('/login'); 
  }

  function onSelectOrg(id: string) {
    setSelectedOrg(id);
    if (id) {
      localStorage.setItem('orgId', id);
    } else {
      // This case should ideally not be reached for superadmin anymore
      localStorage.removeItem('orgId');
    }

    // Store org name for display purposes
    try {
      const name = orgs.find((o) => o.id === id)?.name;
      if (name) localStorage.setItem('orgName', name);
      else localStorage.removeItem('orgName');
    } catch {}
    // Notify same-tab subscribers (Home and others using useCurrentOrgId)
    try { window.dispatchEvent(new Event('org:changed')); } catch {}
    // Invalidate and refetch relevant queries without reloading the page
    qc.invalidateQueries({ predicate: (q) => q.queryKey?.[0] === 'scooters' });
    qc.invalidateQueries({ predicate: (q) => q.queryKey?.[0] === 'clients' });
    qc.invalidateQueries({ predicate: (q) => q.queryKey?.[0] === 'contracts' });
    qc.invalidateQueries({ predicate: (q) => q.queryKey?.[0] === 'summary' });
  }

  return (
    <aside data-testid="sidebar" className={`${isCollapsed ? 'w-16' : 'w-56'} shrink-0 border-r border-border-subtle bg-bg-elevated h-screen sticky top-0 flex flex-col transition-all duration-300 ease-in-out`}>
      <div className={`p-4 flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
        {!isCollapsed && (
          <div className="flex-1">
            <Link to="/dashboard" data-testid="sidebar-logo-link" className="block hover:opacity-80 transition-opacity">
              {!logoError ? (
                <img 
                  src="/logo.png" 
                  alt="Our Scooters" 
                  className="h-10 w-auto object-contain"
                  data-testid="sidebar-logo"
                  onError={() => {
                    // Hide broken image and show text fallback
                    setLogoError(true);
                  }}
                />
              ) : (
                <div className="h-10 flex items-center text-lg font-semibold text-text" data-testid="sidebar-logo-fallback">
                  Our Scooters
                </div>
              )}
            </Link>
            <div className="mt-2 text-sm text-text-dim">
              {user ? (
                user.role === 'superadmin' ? (
                  <select data-testid="sidebar-org-selector" value={selectedOrg ?? ''} onChange={(e) => onSelectOrg(e.target.value)} className="mt-1 text-sm bg-bg-elevated border border-border-subtle rounded px-2 py-1 text-text focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan/50 transition-colors">
                    {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                ) : (
                  <div data-testid="sidebar-org-name" className="mt-1">{user.org?.name ?? '—'}</div>
                )
              ) : (
                // when signed out, only show the Sign in link in the top-right to avoid duplication
                <div className="mt-1">—</div>
              )}
            </div>
          </div>
        )}
        
        {isCollapsed && null}
        
        <div className="flex items-center gap-2">
          {!isCollapsed && (
            <div>
              {user ? null : <Link to="/login" data-testid="sidebar-sign-in-link" className="text-sm text-text-dim">{t('sidebar:signIn')}</Link>}
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleCollapse}
            data-testid="sidebar-toggle-collapse"
            className={`p-2 transition-all duration-300 hover:scale-110 hover:shadow-lg ${isCollapsed ? 'hover:bg-accent-cyan/20 hover:text-accent-cyan' : 'hover:bg-bg-e2'}`}
            title={isCollapsed ? t('sidebar:expand') : t('sidebar:collapse')}
          >
            <span className={`text-xl transition-all duration-300 ${isCollapsed ? 'rotate-0 hover:rotate-12' : 'rotate-180 hover:rotate-168'}`}>
              {isCollapsed ? '🎯' : '📌'}
            </span>
          </Button>
        </div>
      </div>
      <nav data-testid="sidebar-navigation" className="px-2 space-y-1">
        {items.map(i => {
          const active = pathname.startsWith(i.to);
          const activeClass = active
            ? 'bg-accent-cyan/20 text-accent-cyan ring-2 ring-accent-cyan/50 border border-accent-cyan/30 shadow-lg'
            : 'text-text-muted hover:bg-bg-e2 hover:text-text border border-transparent';
          const label = t(`sidebar:${i.key}`);
          const iconClass = i.key === 'scooters' ? 'sidebar-icon-scooter' : i.key === 'clients' ? 'sidebar-icon-client' : 'sidebar-icon-contract';
          return (
            <Link
              key={i.to}
              to={i.to}
              data-testid={`sidebar-nav-${i.key}`}
              onMouseEnter={() => prefetch(i.to)}
              onFocus={() => prefetch(i.to)}
              className={`block ${isCollapsed ? 'px-2 py-3' : 'px-3 py-2'} rounded-lg transition ${activeClass}`}
              title={isCollapsed ? label : undefined}
            >
              <span className={`inline-flex items-center ${isCollapsed ? 'justify-center' : 'gap-2 justify-between'} w-full`}>
                {isCollapsed ? (
                  <span className="text-lg" aria-hidden>{i.icon}</span>
                ) : (
                  <>
                    <span>{label}</span>
                    {active && (
                      <span aria-hidden className={iconClass}>{i.icon}</span>
                    )}
                  </>
                )}
              </span>
            </Link>
          );
        })}
        
        {/* visual divider between general pages and management pages */}
        <div data-testid="sidebar-divider" className="my-3 border-t border-border-subtle" />

        {/* Users: visible to superadmin (all orgs) and admin (their org) - placed below divider */}
        {user && (user.role === 'superadmin' || user.role === 'admin') && (
          <>
            <Link
              to="/users"
              data-testid="sidebar-nav-users"
              className={`block ${isCollapsed ? 'px-2 py-3' : 'px-3 py-2'} rounded-lg transition ${pathname.startsWith('/users')
                ? 'bg-accent-cyan/20 text-accent-cyan ring-2 ring-accent-cyan/50 border border-accent-cyan/30 shadow-lg'
                : 'text-text-muted hover:bg-bg-e2 hover:text-text border border-transparent'}`}
              title={isCollapsed ? t('sidebar:users') : undefined}
            >
              <span className={`inline-flex items-center ${isCollapsed ? 'justify-center' : ''} w-full`}>
                {isCollapsed ? (
                  <span className="text-lg" aria-hidden>👥</span>
                ) : (
                  t('sidebar:users')
                )}
              </span>
            </Link>
            <Link
              to="/user-actions"
              data-testid="sidebar-nav-user-actions"
              className={`block ${isCollapsed ? 'px-2 py-3' : 'px-3 py-2'} rounded-lg transition ${pathname.startsWith('/user-actions')
                ? 'bg-accent-cyan/20 text-accent-cyan ring-2 ring-accent-cyan/50 border border-accent-cyan/30 shadow-lg'
                : 'text-text-muted hover:bg-bg-e2 hover:text-text border border-transparent'}`}
              title={isCollapsed ? t('sidebar:activity') : undefined}
            >
              <span className={`inline-flex items-center ${isCollapsed ? 'justify-center' : ''} w-full`}>
                {isCollapsed ? (
                  <span className="text-lg" aria-hidden>📊</span>
                ) : (
                  t('sidebar:activity')
                )}
              </span>
            </Link>
            <Link
              to="/management"
              data-testid="sidebar-nav-management"
              className={`block ${isCollapsed ? 'px-2 py-3' : 'px-3 py-2'} rounded-lg transition ${pathname.startsWith('/management')
                ? 'bg-accent-cyan/20 text-accent-cyan ring-2 ring-accent-cyan/50 border border-accent-cyan/30 shadow-lg'
                : 'text-text-muted hover:bg-bg-e2 hover:text-text border border-transparent'}`}
              title={isCollapsed ? t('sidebar:management') : undefined}
            >
              <span className={`inline-flex items-center ${isCollapsed ? 'justify-center' : ''} w-full`}>
                {isCollapsed ? (
                  <span className="text-lg" aria-hidden>⚙️</span>
                ) : (
                  t('sidebar:management')
                )}
              </span>
            </Link>
            {/* Analytics: visible to superadmin only */}
            {user && user.role === 'superadmin' && (
              <Link
                to="/analytics"
                data-testid="sidebar-nav-analytics"
                className={`block ${isCollapsed ? 'px-2 py-3' : 'px-3 py-2'} rounded-lg transition ${pathname.startsWith('/analytics')
                  ? 'bg-accent-cyan/20 text-accent-cyan ring-2 ring-accent-cyan/50 border border-accent-cyan/30 shadow-lg'
                  : 'text-text-muted hover:bg-bg-e2 hover:text-text border border-transparent'}`}
                title={isCollapsed ? 'Analytics' : undefined}
              >
                <span className={`inline-flex items-center ${isCollapsed ? 'justify-center' : ''} w-full`}>
                  {isCollapsed ? (
                    <span className="text-lg" aria-hidden>📈</span>
                  ) : (
                    'Analytics'
                  )}
                </span>
              </Link>
            )}
            {/* Business Details link moved into Management page as a button */}
          </>
        )}
      </nav>
      {/* logout (if signed in) and theme toggle placed at the bottom */}
      {!isCollapsed && (
        <div data-testid="sidebar-footer" className="mt-auto p-4 border-t border-border-subtle flex flex-col items-stretch gap-3">
          {user && (
            <div className="flex items-center gap-2">
              <Button 
                variant="danger" 
                onClick={onLogout} 
                size="sm"
                data-testid="sidebar-logout-button"
                className="flex-1 justify-center"
              >
                {t('common:logout', { defaultValue: 'Logout' })}
              </Button>
              <LanguageSwitcher />
            </div>
          )}
          <div data-testid="sidebar-theme-toggle-container" className="flex items-center justify-center">
            <ThemeToggle />
          </div>
          <div className="pt-2">
            <VersionDisplay className="text-[10px] text-text-dim opacity-50 hover:opacity-100 transition-opacity" />
          </div>
        </div>
      )}
      
      {/* Collapsed mode: no logout button to avoid confusion with collapse button */}
      
      {/* Logout confirmation dialog */}
      <ConfirmDialog
        open={logoutConfirmOpen}
        onClose={() => setLogoutConfirmOpen(false)}
        title={t('common:logout', { defaultValue: 'Logout' })}
        message={t('sidebar:logoutConfirm', { defaultValue: 'Are you sure you want to logout?' })}
        confirmLabel={t('common:logout', { defaultValue: 'Logout' })}
        cancelLabel={t('common:cancel', { defaultValue: 'Cancel' })}
        tone="danger"
        onConfirm={handleConfirmLogout}
        data-testid="sidebar-logout-confirm-dialog"
      />
    </aside>
  );
}
